import { Check, Eye, Filter } from 'lucide-react'
import {
  BROWSE_INACTIVE_DAY_OPTIONS,
  type BrowseInactiveDays,
  type FolderBrowseMode,
} from '@/lib/browse-folder-focus'
import { BrowseFolderVisibilityPill } from '@/components/features/browse-folder-visibility-pill'
import { cn } from '@/lib/utils'

/** Checkbox row that does not close the fixed context menu on click. */
export function BrowseMenuToggleRow({
  checked,
  onCheckedChange,
  children,
  disabled,
}: {
  checked: boolean
  onCheckedChange: (v: boolean) => void
  children: React.ReactNode
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'w-full flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground transition-colors text-left',
        disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
      )}
      onPointerDown={(e) => e.preventDefault()}
      onClick={() => onCheckedChange(!checked)}
    >
      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
        {checked ? <Check className="h-3.5 w-3.5" /> : null}
      </span>
      <span className="flex-1">{children}</span>
    </button>
  )
}

type BrowseDocumentsMenuBlockProps = {
  focusBrowse: boolean
  onFocusBrowseChange: (v: boolean) => void
  inactiveDays: BrowseInactiveDays
  onInactiveDaysChange: (d: BrowseInactiveDays) => void
  showActivityInTree: boolean
  onShowActivityInTreeChange: (v: boolean) => void
  onOpenBrowsePreferences: () => void
}

export function BrowseDocumentsMenuBlock({
  focusBrowse,
  onFocusBrowseChange,
  inactiveDays,
  onInactiveDaysChange,
  showActivityInTree,
  onShowActivityInTreeChange,
  onOpenBrowsePreferences,
}: BrowseDocumentsMenuBlockProps) {
  return (
    <>
      <div className="h-px bg-border my-0.5" />
      <div className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
        Browse
      </div>
      <BrowseMenuToggleRow checked={focusBrowse} onCheckedChange={onFocusBrowseChange}>
        Focus browse
      </BrowseMenuToggleRow>
      <div className="px-2 py-0.5">
        <p className="text-[10px] text-muted-foreground px-0.5 pb-1">Hide inactive for</p>
        <div className="flex flex-wrap gap-0.5">
          {BROWSE_INACTIVE_DAY_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              disabled={!focusBrowse}
              className={cn(
                'rounded px-1.5 py-0.5 text-[10px] border transition-colors',
                inactiveDays === d
                  ? 'bg-primary/15 border-primary/40 text-foreground'
                  : 'border-transparent text-muted-foreground hover:bg-accent',
                !focusBrowse && 'opacity-40 pointer-events-none'
              )}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => onInactiveDaysChange(d)}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      <BrowseMenuToggleRow
        checked={showActivityInTree}
        onCheckedChange={onShowActivityInTreeChange}
      >
        Show activity in tree
      </BrowseMenuToggleRow>
      <button
        type="button"
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground transition-colors text-left"
        onClick={onOpenBrowsePreferences}
      >
        <Filter className="h-4 w-4 shrink-0" />
        Browse preferences...
      </button>
    </>
  )
}

export function BrowseFolderVisibilityMenuBlock({
  mode,
  onModeChange,
  focusBrowseEnabled = true,
}: {
  mode: FolderBrowseMode
  onModeChange: (mode: FolderBrowseMode) => void
  /** When global focus browse is off, per-folder overrides do not apply. */
  focusBrowseEnabled?: boolean
}) {
  return (
    <>
      <div className="h-px bg-border my-0.5" />
      <div className={cn(!focusBrowseEnabled && 'opacity-50 pointer-events-none')}>
        <BrowseFolderVisibilityPill mode={mode} onModeChange={onModeChange} />
        {!focusBrowseEnabled ? (
          <p className="px-2 pb-1 text-[10px] text-muted-foreground">
            Turn on Focus browse on Documents to use per-folder visibility.
          </p>
        ) : null}
      </div>
    </>
  )
}

export function BrowseShowAllHiddenRow({
  hiddenCount,
  label,
  onShowAll,
}: {
  hiddenCount: number
  /** e.g. "Show 12 hidden files" - defaults to folder wording */
  label?: string
  onShowAll: () => void
}) {
  const text =
    label ?? (hiddenCount === 1 ? 'Show 1 hidden folder' : `Show ${hiddenCount} hidden folders`)
  return (
    <>
      <div className="h-px bg-border my-0.5" />
      <button
        type="button"
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground transition-colors text-left"
        onClick={onShowAll}
      >
        <Eye className="h-4 w-4 shrink-0" />
        {text}
      </button>
    </>
  )
}
